import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Dialog, Form } from "@sscs/ui";
import { MultiEmailField } from "Components";
import { SharedReportForm } from "../../../../../types";
import { EMAIL_MESSAGE_PLACEHOLDER } from "../../../../../utils";
import Label from "../../Label/Label";
import styles from "./recipientsStep.module.scss";

type RecipientsStepProps = {
  recipientsRequired?: boolean;
  onSkipRecipients?: (optional: boolean) => void;
};

const RecipientsStep = ({
  recipientsRequired = false,
  onSkipRecipients,
}: RecipientsStepProps): React.JSX.Element => {
  const [pendingInput, setPendingInput] = useState("");
  const formMethods = useFormContext<SharedReportForm>();
  const { formState, reset, watch } = formMethods;
  const recipients = watch("recipients");
  const skipRecipientsStep =
    !recipientsRequired &&
    recipients?.length === 0 &&
    pendingInput.trim() === "";

  useEffect(() => {
    onSkipRecipients?.(skipRecipientsStep);
  }, [onSkipRecipients, skipRecipientsStep]);

  // Handle an edge case where user has entered more than allowed character limit in a message,
  // then cleared the email field and then tried to skip the step (hits error on disabled field) [SWAP-1818]
  useEffect(() => {
    if (skipRecipientsStep && formState.errors["message"]) {
      reset();
    }
  }, [formState.errors, reset, skipRecipientsStep]);

  return (
    <Dialog.Section>
      <Dialog.Section.Content>
        <MultiEmailField
          id="recipients-email-field"
          fieldName="recipients"
          label="Recipients"
          required={recipientsRequired}
          helperText="Recipients will receive a link and password to the analysis report
            together with your message"
          className={styles["multi-email-field"]}
          onPendingInputChange={setPendingInput}
        />
        <div className={styles["message-field"]}>
          <Label
            label="Customize your message"
            isOptional
            isDisabled={skipRecipientsStep}
          />
          <Form.TextArea
            name="message"
            errors={formState.errors}
            isDisabled={skipRecipientsStep}
            placeholder={EMAIL_MESSAGE_PLACEHOLDER}
            minRows={9}
            maxRows={9}
            className={styles["custom-message"]}
          />
        </div>
        <Form.Checkbox
          name="send_copy_to_me"
          label="Send a copy to me"
          className={styles["form-checkbox"]}
          isDisabled={skipRecipientsStep}
          errors={formState.errors}
        />
      </Dialog.Section.Content>
    </Dialog.Section>
  );
};

export default RecipientsStep;
